import React, { useEffect, useState } from 'react';
import { Box, Typography, Modal } from '@mui/material';
import { Button, GradientButton, Input, SelectOption } from '..';
import { getTakeDownRejectionReasons } from '../../queries/takedowns';
import { sanitizeText } from '../../utils';

const OTHER_REASON_ID = '9';
// Matched by label: reason ids are not guaranteed to match across envs.
const BENIGN_REASON_LABEL = 'benign content';
const MAX_CUSTOM_REASON_LENGTH = 500;

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  border: 'none !important',
  borderRadius: '10px',
  boxShadow: 24,
  p: 4,
};

const buttonWrapper = {
  display: 'flex',
  gap: 2,
  alignItems: 'center',
  justifyContent: 'center',
  mt: 2,
};

interface RejectionReasonsModalProps {
  open: boolean;
  handleClose: () => void;
  handleConfirm: (
    rejectionReasonId: number,
    customRejectionReason?: string,
  ) => void;
  /** Target status. 'benign' preselects the Benign Content reason (WHEEL-404). */
  status?: string;
}

const RejectionReasonsModal = ({
  open,
  handleClose,
  handleConfirm,
  status,
}: RejectionReasonsModalProps) => {
  const [rejectionReasons, setRejectionReasons] = useState<any[]>([]);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [customRejectionReason, setCustomRejectionReason] = useState('');
  const [customReasonError, setCustomReasonError] = useState('');

  const isOtherReasonSelected = selectedReason === OTHER_REASON_ID;

  useEffect(() => {
    const fetchRejectionReasons = async () => {
      try {
        const response = await getTakeDownRejectionReasons();
        if (response?.data) {
          const reasons = response.data.map((data: any) => ({
            label: data.reason,
            value: String(data.id),
          }));
          setRejectionReasons(reasons);
        }
      } catch (err) {
        console.error(err);
      }
    };
    if (open) {
      fetchRejectionReasons();
    }
  }, [open]);

  useEffect(() => {
    if (!open || status !== 'benign' || selectedReason) return;
    const benignReason = rejectionReasons.find(
      reason => reason.label?.trim().toLowerCase() === BENIGN_REASON_LABEL,
    );
    if (benignReason) setSelectedReason(benignReason.value);
    // Only on open/reasons load: the analyst can still pick another reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, status, rejectionReasons]);

  const validateCustomReason = (value: string): string => {
    if (value.length > MAX_CUSTOM_REASON_LENGTH) {
      return `Custom rejection reason must not exceed ${MAX_CUSTOM_REASON_LENGTH} characters`;
    }
    return '';
  };

  const handleCustomReasonChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = e.target.value;
    setCustomRejectionReason(value);
    setCustomReasonError(validateCustomReason(value));
  };

  const canSubmit = (): boolean => {
    if (!selectedReason) return false;
    if (isOtherReasonSelected) {
      return (
        customRejectionReason.trim().length > 0 &&
        customRejectionReason.length <= MAX_CUSTOM_REASON_LENGTH
      );
    }
    return true;
  };

  const handleSubmit = () => {
    const customReason =
      isOtherReasonSelected && customRejectionReason.trim()
        ? sanitizeText(customRejectionReason)
        : undefined;
    handleConfirm(Number(selectedReason), customReason);
    resetState();
  };

  const resetState = () => {
    setSelectedReason('');
    setCustomRejectionReason('');
    setCustomReasonError('');
  };

  const onClose = () => {
    resetState();
    handleClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style} style={{ width: 450 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Rejection Reason
        </Typography>
        <Typography sx={{ mt: 1 }}>
          Please select a reason for rejecting this threat.
        </Typography>
        <Box sx={{ display: 'flex', mt: 2 }}>
          <Typography gutterBottom sx={{ color: 'text.primary', width: '30%' }}>
            Reason
          </Typography>
          <SelectOption
            label="rejection reason"
            size="small"
            onChange={(e: any) => setSelectedReason(e.target.value)}
            value={selectedReason}
            options={rejectionReasons}
            width="100%"
          />
        </Box>
        {isOtherReasonSelected && (
          <Box sx={{ display: 'flex', mt: 2 }}>
            <Typography
              gutterBottom
              sx={{ color: 'text.primary', width: '30%' }}
            >
              Custom Reason *
            </Typography>
            <Input
              placeholder="Please provide a custom rejection reason"
              size="small"
              variant="outlined"
              multiline
              rows={3}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  height: 'auto',
                  padding: '8px 12px',
                },
              }}
              value={customRejectionReason}
              onChange={handleCustomReasonChange}
              error={Boolean(customReasonError)}
              helperText={
                customReasonError ||
                `${customRejectionReason.length}/${MAX_CUSTOM_REASON_LENGTH} characters`
              }
            />
          </Box>
        )}
        <Box sx={buttonWrapper}>
          <Button onClick={onClose} variant="outlined" label="Cancel" />
          <GradientButton
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit()}
          >
            Confirm
          </GradientButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default RejectionReasonsModal;
